import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  DAILY_PAGE_SUPER_OVER_OBJECTIVES,
  DEFAULT_CHALLENGE_DEEPLINKS,
  GAME_TYPES,
  buildChallengePayload,
  challengeToForm,
  emptyChallengeForm,
  emptyFastestFingerRound,
  emptyQuizQuestion,
  validateChallengeForm,
} from './dailyTasksUtils';

function ChallengeFormModal({
  open,
  onClose,
  onSubmit,
  challenge,
  initialGameType = 'quiz',
  lockGameType = false,
  challengeTypes = [],
  saving,
}) {
  const [form, setForm] = useState(() => emptyChallengeForm(initialGameType));
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (challenge) setForm(challengeToForm(challenge));
    else setForm(emptyChallengeForm(initialGameType || 'quiz'));
    setErrors([]);
  }, [open, challenge, initialGameType]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateConfig = (patch) =>
    setForm((prev) => ({ ...prev, game_config: { ...prev.game_config, ...patch } }));

  const handleTypeChange = (game_type) => {
    const next = emptyChallengeForm(game_type);
    setForm({
      ...next,
      title: form.title,
      subtitle: form.subtitle,
      card_background_image: form.card_background_image,
      game_icon: form.game_icon,
      fe_prize_per_participation: form.fe_prize_per_participation,
      redirection_link: form.redirection_link,
    });
  };

  const handleSave = () => {
    const errs = validateChallengeForm(form);
    setErrors(errs);
    if (errs.length) return;
    onSubmit(buildChallengePayload(form));
  };

  const questions = form.game_config?.questions || [];
  const rounds = form.game_config?.rounds || [];
  const typeMeta = GAME_TYPES.find((g) => g.value === form.game_type);
  const typeHelp = typeMeta?.notes || '';
  const typeLabel = typeMeta?.label || form.game_type;
  const objectiveOptions =
    Array.isArray(challengeTypes) && challengeTypes.length > 0
      ? challengeTypes
      : DAILY_PAGE_SUPER_OVER_OBJECTIVES;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {challenge ? `Edit Daily Page · ${typeLabel}` : `Add Daily Page · ${typeLabel}`}
      </DialogTitle>
      <DialogContent>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {errors.join(' ')}
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
          Saves to Daily Page activities only (<code>config.surface=daily_page</code>). Creates a
          playable <code>games</code> row with <code>category=daily_page</code> (hidden from Arcade{' '}
          <code>GET /games</code>). Never inserts Arcade Super Over Daily — that stays on{' '}
          <code>/admin/arcade/super-over-daily</code>.
        </Alert>
        {challenge?.game_id || form.game_id ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <code>game_id</code>: {challenge?.game_id || form.game_id}
            {(challenge?.deeplink || form.deeplink || form.redirection_link) && (
              <>
                {' '}
                · deeplink:{' '}
                <code>
                  {challenge?.deeplink || form.deeplink || form.redirection_link}
                </code>
              </>
            )}
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Game type"
              value={form.game_type}
              onChange={(e) => handleTypeChange(e.target.value)}
              helperText={`config.game_type · ${typeHelp}`}
              disabled={!!challenge || lockGameType}
            >
              {GAME_TYPES.map((g) => (
                <MenuItem key={g.value} value={g.value}>
                  {g.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Final FE reward"
              value={form.fe_prize_per_participation}
              onChange={(e) => update({ fe_prize_per_participation: Number(e.target.value) })}
              helperText="fe_prize_per_participation"
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Title"
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Subtitle"
              value={form.subtitle}
              onChange={(e) => update({ subtitle: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Redirection link (optional)"
              value={form.redirection_link}
              onChange={(e) => update({ redirection_link: e.target.value })}
              helperText={
                DEFAULT_CHALLENGE_DEEPLINKS[form.game_type]
                  ? `Leave blank → BE auto: ${DEFAULT_CHALLENGE_DEEPLINKS[form.game_type]}`
                  : 'Leave blank for BE default deeplink'
              }
              placeholder={DEFAULT_CHALLENGE_DEEPLINKS[form.game_type] || ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Card background image"
              value={form.card_background_image}
              onChange={(e) => update({ card_background_image: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Game icon"
              value={form.game_icon}
              onChange={(e) => update({ game_icon: e.target.value })}
            />
          </Grid>

          {form.game_type === 'quiz' && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Countdown seconds"
                  value={form.game_config.countdown_seconds ?? 45}
                  onChange={(e) => updateConfig({ countdown_seconds: Number(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Questions (exactly 5)
                </Typography>
                {questions.map((q, qi) => (
                  <Box
                    key={q.question_id || qi}
                    sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Question {qi + 1}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label="Question text"
                      value={q.question_text || ''}
                      sx={{ mt: 1, mb: 1 }}
                      onChange={(e) => {
                        const next = questions.map((row, i) =>
                          i === qi ? { ...row, question_text: e.target.value, question_id: qi + 1 } : row
                        );
                        while (next.length < 5) next.push(emptyQuizQuestion(next.length + 1));
                        updateConfig({ questions: next.slice(0, 5) });
                      }}
                    />
                    <Grid container spacing={1}>
                      {['1', '2', '3', '4'].map((k) => (
                        <Grid item xs={6} key={k}>
                          <TextField
                            fullWidth
                            size="small"
                            label={`Option ${k}`}
                            value={q.options?.[k] || ''}
                            onChange={(e) => {
                              const next = questions.map((row, i) =>
                                i === qi
                                  ? {
                                      ...row,
                                      options: { ...(row.options || {}), [k]: e.target.value },
                                    }
                                  : row
                              );
                              updateConfig({ questions: next });
                            }}
                          />
                        </Grid>
                      ))}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Correct answer"
                          value={q.correct_answer ?? 1}
                          onChange={(e) => {
                            const next = questions.map((row, i) =>
                              i === qi ? { ...row, correct_answer: Number(e.target.value) } : row
                            );
                            updateConfig({ questions: next });
                          }}
                        >
                          {[1, 2, 3, 4].map((n) => (
                            <MenuItem key={n} value={n}>
                              {n}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Grid>
            </>
          )}

          {form.game_type === 'fastest_finger' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Rounds (exactly 5) — answers are uppercased on save
              </Typography>
              {rounds.map((r, ri) => (
                <Box key={r.round_id || ri} display="flex" gap={1} mb={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Round ${ri + 1} prompt`}
                    value={r.prompt || ''}
                    onChange={(e) => {
                      const next = rounds.map((row, i) =>
                        i === ri ? { ...row, prompt: e.target.value, round_id: ri + 1 } : row
                      );
                      while (next.length < 5) next.push(emptyFastestFingerRound(next.length + 1));
                      updateConfig({ rounds: next.slice(0, 5) });
                    }}
                  />
                  <TextField
                    size="small"
                    label="Correct answer"
                    value={r.correct_answer || ''}
                    onChange={(e) => {
                      const next = rounds.map((row, i) =>
                        i === ri ? { ...row, correct_answer: e.target.value } : row
                      );
                      updateConfig({ rounds: next });
                    }}
                    sx={{ width: 200 }}
                  />
                </Box>
              ))}
            </Grid>
          )}

          {form.game_type === 'super_over' && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Super Over game_config</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Objective / challenge type"
                  value={form.game_config.challenge_type || form.game_config.objective || ''}
                  onChange={(e) =>
                    updateConfig({
                      challenge_type: e.target.value,
                      objective: e.target.value,
                    })
                  }
                  helperText="Daily Page objectives (not Arcade admin)"
                >
                  <MenuItem value="">— select —</MenuItem>
                  {objectiveOptions.map((t) => {
                    const value = typeof t === 'string' ? t : t.value || t.key || t.id;
                    const label = typeof t === 'string' ? t : t.label || value;
                    return (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="success_config (JSON)"
                  value={JSON.stringify(form.game_config.success_config || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value || '{}');
                      updateConfig({ success_config: parsed });
                    } catch {
                      /* keep typing */
                    }
                  }}
                  helperText="On save, shell title / FE / image sync into reward_fe + image_url"
                />
              </Grid>
            </>
          )}

          {['pitch_word', 'snapshot', 'run_connect'].includes(form.game_type) && (
            <Grid item xs={12}>
              <Alert severity="info">
                Launcher card — title / FE / images; <code>game_config</code> is empty. Deeplink is
                auto-filled if you leave redirection blank.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChallengeFormModal;
